import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * CorrectionModalBase - shared base for sales and purchase invoice correction modals.
 *
 * Props (config):
 *   isOpen, onClose, invoice, onCorrectionComplete  -- standard modal props
 *   correctionReasons      -- array of { value, label }
 *   title                  -- modal header text (function of invoice)
 *   apiBase                -- e.g. "sales-invoices" or "purchase-invoices"
 *   itemsKey               -- key inside API response holding line-items ("pozycje" | "items")
 *   priceField             -- field name on item for unit price ("cena_jednostkowa_brutto" | "cena_netto")
 *   priceColumnLabels      -- { original, new } header strings
 *   invoiceInfoRows        -- function(invoice) => array of { label, value }
 *   itemSubtitle           -- function(item) => ReactNode for extra info under product name
 *   calculateRowDifference -- function(item, sel) => number  (brutto difference for that row)
 *   calculateTotal         -- function(items, selectedItems) => number (total correction value)
 *   differenceColorClasses -- { negative, positive } css classes for the difference column
 *   summaryColors          -- { refund, surcharge } background colors for the summary card
 *   summaryLabels          -- { refund, surcharge } text labels
 *   summaryTextClasses     -- { refund, surcharge } css classes for amount text
 *   buildCorrectionItem    -- function(item, sel, { iloscKorekty, getFullReason }) => object for API
 *   buildSubmitBody        -- function({ correctionItems, getFullReason, extraState, authUser }) => object
 *   onSuccessData          -- function(apiData, { extraState, invoice }) => { correctionData, successMessage }
 *   renderExtraFields      -- function({ extraState, setExtraState, disabled }) => ReactNode | null
 *   renderPaymentSection   -- function({ paymentMethod, setPaymentMethod, disabled }) => ReactNode
 *   handlePrint            -- function({ correctionData, invoice, calculateCorrectionTotal, extraState }) => void
 *   defaultPaymentMethod   -- string, default payment method value
 *   extraStateDefaults     -- object, extra state fields to manage (e.g. { externalCorrectionNumber: '', paymentDays: 14 })
 *   correctionNumberKey    -- key in API response for the correction number (default "numer_faktury")
 */
const CorrectionModalBase = ({
  isOpen,
  onClose,
  invoice,
  onCorrectionComplete,
  // Configuration
  correctionReasons,
  title,
  apiBase,
  itemsKey,
  priceField,
  priceColumnLabels,
  invoiceInfoRows,
  itemSubtitle,
  calculateRowDifference,
  calculateTotal,
  differenceColorClasses,
  summaryColors,
  summaryLabels,
  summaryTextClasses,
  buildCorrectionItem,
  buildSubmitBody,
  onSuccessData,
  renderExtraFields,
  renderPaymentSection,
  handlePrint,
  defaultPaymentMethod = 'gotowka',
  extraStateDefaults = {},
}) => {
  const { user: authUser } = useAuth();
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethod);
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [correctionData, setCorrectionData] = useState(null);
  const [extraState, setExtraState] = useState({ ...extraStateDefaults });

  // Reset on open
  useEffect(() => {
    if (isOpen && invoice?.id) {
      loadInvoiceItems();
      setCorrectionData(null);
      setSuccess('');
      setError('');
      setReason('');
      setCustomReason('');
      setPaymentMethod(defaultPaymentMethod);
      setExtraState({ ...extraStateDefaults });
    }
  }, [isOpen, invoice]);

  const loadInvoiceItems = async () => {
    setLoadingItems(true);
    setError('');

    try {
      const response = await api.get(`/${apiBase}/${invoice.id}`);
      const data = response.data;

      if (data.success) {
        const invoiceData = data.data;
        const itemsData = invoiceData[itemsKey] || [];
        setItems(itemsData);

        const initial = {};
        itemsData.forEach(item => {
          initial[item.id] = {
            selected: false,
            quantity: parseFloat(item.ilosc) || 0,
            maxQuantity: parseFloat(item.ilosc) || 0,
            originalPrice: parseFloat(item[priceField]) || 0,
            newPrice: parseFloat(item[priceField]) || 0,
            reason: ''
          };
        });
        setSelectedItems(initial);
      } else {
        setError(data.message || data.error || 'Błąd pobierania pozycji faktury');
      }
    } catch (err) {
      console.error('Błąd:', err);
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleItem = (itemId) => {
    setSelectedItems(prev => {
      const current = prev[itemId];
      const newSelected = !current.selected;
      return {
        ...prev,
        [itemId]: {
          ...current,
          selected: newSelected,
          quantity: newSelected ? 0 : current.maxQuantity
        }
      };
    });
  };

  const updateQuantity = (itemId, value) => {
    const qty = parseFloat(value) || 0;
    const maxQty = selectedItems[itemId]?.maxQuantity || 0;

    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.min(Math.max(0, qty), maxQty)
      }
    }));
  };

  const updateNewPrice = (itemId, value) => {
    const price = parseFloat(value) || 0;

    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        newPrice: Math.max(0, price)
      }
    }));
  };

  const selectAll = () => {
    setSelectedItems(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (updated[key].maxQuantity > 0) {
          updated[key].selected = true;
          updated[key].quantity = 0;
        }
      });
      return updated;
    });
  };

  const deselectAll = () => {
    setSelectedItems(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key].selected = false;
        updated[key].quantity = updated[key].maxQuantity;
      });
      return updated;
    });
  };

  const correctionTotal = calculateTotal(items, selectedItems);
  const isRefund = correctionTotal < 0;

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(s => s.selected).length;
  };

  const getFullReason = () => {
    if (reason === 'inne') {
      return customReason || 'Inny powód';
    }
    const found = correctionReasons.find(r => r.value === reason);
    return found ? found.label : reason;
  };

  const handleSubmit = async () => {
    if (!reason) {
      setError('Wybierz powód korekty');
      return;
    }

    if (reason === 'inne' && !customReason.trim()) {
      setError('Opisz powód korekty');
      return;
    }

    const correctionItems = [];

    items.forEach(item => {
      const sel = selectedItems[item.id];
      if (sel?.selected) {
        const originalQty = parseFloat(item.ilosc);
        const newQty = sel.quantity;
        const iloscKorekty = newQty - originalQty;

        correctionItems.push(
          buildCorrectionItem(item, sel, { iloscKorekty, originalQty, newQty, getFullReason })
        );
      }
    });

    if (correctionItems.length === 0) {
      setError('Wybierz przynajmniej jedną pozycję do korekty');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const body = buildSubmitBody({
        correctionItems,
        getFullReason,
        paymentMethod,
        extraState,
        authUser
      });

      const response = await api.post(`/${apiBase}/${invoice.id}/correction`, body);

      const data = response.data;

      if (data.success) {
        const result = onSuccessData(data.data, {
          correctionItems,
          getFullReason,
          paymentMethod,
          extraState,
          invoice
        });

        setCorrectionData(result.correctionData);
        setSuccess(result.successMessage);
      } else {
        setError(data.message || data.error || 'Błąd tworzenia korekty');
      }
    } catch (err) {
      console.error('Błąd:', err);
      setError(err.message || 'Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintCorrection = () => {
    if (!correctionData) return;
    handlePrint({
      correctionData,
      invoice,
      correctionTotal,
      extraState
    });
  };

  const handleClose = () => {
    if (correctionData) {
      onCorrectionComplete && onCorrectionComplete(correctionData);
    }
    onClose();
  };

  if (!isOpen) return null;

  const infoRows = invoiceInfoRows(invoice);

  return (
    <>
      <div
        className="modal fade show"
        style={{
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1050,
          overflow: 'auto'
        }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content" style={{ fontSize: '12px' }}>
            <div className="modal-header py-2" style={{ backgroundColor: 'var(--warning)', color: '#000' }}>
              <h6 className="modal-title mb-0" style={{ fontSize: '13px' }}>
                <i className="fas fa-edit me-2"></i>
                {title(invoice)}
              </h6>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>

            <div className="modal-body py-2" style={{ fontSize: '11px' }}>
              {error && (
                <div className="alert alert-danger alert-dismissible fade show py-1 mb-2" style={{ fontSize: '11px' }}>
                  <i className="fas fa-exclamation-circle me-1"></i>
                  {error}
                  <button type="button" className="btn-close" style={{ padding: '0.5rem' }} onClick={() => setError('')}></button>
                </div>
              )}

              {success && (
                <div className="alert alert-success py-1 mb-2" style={{ fontSize: '11px' }}>
                  <i className="fas fa-check-circle me-1"></i>
                  {success}
                </div>
              )}

              {/* Invoice info card */}
              <div className="card mb-2">
                <div className="card-body py-1 px-2">
                  <div className="row">
                    {infoRows.map((row, idx) => (
                      <div className="col-md-4" key={idx}>
                        <small className="text-muted" style={{ fontSize: '10px' }}>{row.label}:</small>
                        <div style={{ fontSize: '11px' }}><strong>{row.value}</strong></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Items table */}
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold" style={{ fontSize: '11px' }}>Pozycje do korekty:</span>
                  <div>
                    <button
                      className="btn btn-outline-primary btn-sm me-1 py-0 px-2"
                      onClick={selectAll}
                      disabled={loadingItems}
                      style={{ fontSize: '10px' }}
                    >
                      Zaznacz wszystkie
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm py-0 px-2"
                      onClick={deselectAll}
                      disabled={loadingItems}
                      style={{ fontSize: '10px' }}
                    >
                      Odznacz wszystkie
                    </button>
                  </div>
                </div>

                {loadingItems ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                    <p className="mt-1 mb-0 text-muted" style={{ fontSize: '10px' }}>Ładowanie pozycji...</p>
                  </div>
                ) : items.length === 0 ? (
                  <div className="alert alert-warning py-1 mb-0" style={{ fontSize: '11px' }}>
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Brak pozycji do wyświetlenia
                  </div>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: '250px' }}>
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '11px' }}>
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: '30px', padding: '4px' }}></th>
                          <th style={{ padding: '4px' }}>Produkt</th>
                          <th className="text-center" style={{ width: '80px', padding: '4px' }}>{priceColumnLabels.original}</th>
                          <th className="text-center" style={{ width: '80px', padding: '4px' }}>{priceColumnLabels.new}</th>
                          <th className="text-center" style={{ width: '60px', padding: '4px' }}>Ilość oryg.</th>
                          <th className="text-center" style={{ width: '70px', padding: '4px' }}>Nowa ilość</th>
                          <th className="text-center" style={{ width: '90px', padding: '4px' }}>Różnica</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => {
                          const sel = selectedItems[item.id] || {};
                          const difference = calculateRowDifference(item, sel);

                          return (
                            <tr
                              key={item.id}
                              className={sel.selected ? 'table-warning' : ''}
                            >
                              <td className="text-center" style={{ padding: '4px' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={sel.selected || false}
                                  onChange={() => toggleItem(item.id)}
                                  style={{ width: '14px', height: '14px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <div style={{ fontSize: '11px' }}><strong>{item.nazwa_produktu}</strong></div>
                                <small className="text-muted" style={{ fontSize: '9px' }}>
                                  {itemSubtitle(item)}
                                </small>
                              </td>
                              <td className="text-center" style={{ padding: '4px', fontSize: '11px' }}>
                                {parseFloat(item[priceField]).toFixed(2)} zł
                              </td>
                              <td className="text-center" style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-center"
                                  value={sel.newPrice || 0}
                                  onChange={(e) => updateNewPrice(item.id, e.target.value)}
                                  min="0"
                                  step="0.01"
                                  disabled={!sel.selected}
                                  style={{ width: '70px', margin: '0 auto', fontSize: '10px', padding: '2px' }}
                                />
                              </td>
                              <td className="text-center" style={{ padding: '4px', fontSize: '11px' }}>
                                {parseFloat(item.ilosc).toFixed(2)}
                              </td>
                              <td className="text-center" style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-center"
                                  value={sel.quantity || 0}
                                  onChange={(e) => updateQuantity(item.id, e.target.value)}
                                  min="0"
                                  max={sel.maxQuantity || 0}
                                  step="1"
                                  disabled={!sel.selected}
                                  style={{ width: '55px', margin: '0 auto', fontSize: '10px', padding: '2px' }}
                                />
                              </td>
                              <td className="text-center" style={{ padding: '4px', fontSize: '11px' }}>
                                {sel.selected ? (
                                  <strong className={difference < 0 ? differenceColorClasses.negative : difference > 0 ? differenceColorClasses.positive : ''}>
                                    {difference >= 0 ? '+' : ''}{difference.toFixed(2)} zł
                                  </strong>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Payment section + reason */}
              <div className="row mb-2">
                {renderPaymentSection({
                  paymentMethod,
                  setPaymentMethod,
                  reason,
                  setReason,
                  customReason,
                  setCustomReason,
                  correctionReasons,
                  extraState,
                  setExtraState,
                  disabled: loading
                })}
              </div>

              {/* Extra fields (purchase-specific: external number, payment days, etc.) */}
              {renderExtraFields && renderExtraFields({
                extraState,
                setExtraState,
                paymentMethod,
                setPaymentMethod,
                disabled: loading
              })}

              {/* Summary */}
              <div className="card" style={{ backgroundColor: isRefund ? summaryColors.refund : summaryColors.surcharge }}>
                <div className="card-body py-2 px-3">
                  <div className="row align-items-center">
                    <div className="col">
                      <span className="text-muted" style={{ fontSize: '10px' }}>Pozycje do korekty:</span>
                      <strong className="ms-1" style={{ fontSize: '12px' }}>{getSelectedCount()}</strong>
                    </div>
                    <div className="col text-end">
                      <span style={{ fontSize: '10px' }}>
                        {isRefund ? summaryLabels.refund : summaryLabels.surcharge}
                      </span>
                      <span
                        className={`ms-1 fw-bold ${isRefund ? summaryTextClasses.refund : summaryTextClasses.surcharge}`}
                        style={{ fontSize: '16px' }}
                      >
                        {Math.abs(correctionTotal).toFixed(2)} zł
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer py-2">
              {correctionData ? (
                <>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handlePrintCorrection}
                    style={{ fontSize: '11px' }}
                  >
                    <i className="fas fa-print me-1"></i>
                    Drukuj korektę
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleClose}
                    style={{ fontSize: '11px' }}
                  >
                    <i className="fas fa-check me-1"></i>
                    Zamknij
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleClose}
                    disabled={loading}
                    style={{ fontSize: '11px' }}
                  >
                    <i className="fas fa-times me-1"></i>
                    Anuluj
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning btn-sm"
                    onClick={handleSubmit}
                    disabled={loading || getSelectedCount() === 0 || !reason}
                    style={{ fontSize: '11px' }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Przetwarzanie...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-edit me-1"></i>
                        Utwórz korektę ({Math.abs(correctionTotal).toFixed(2)} zł)
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
    </>
  );
};

export default CorrectionModalBase;
